/**
 * Criteo DataLayer ユーティリティ
 * ログインユーザーのメールアドレスをSHA-256でハッシュ化してCriteoに送信する
 */

interface CriteoEvent {
  event: string
  crto: {
    email: string
    products?: Array<{ id: string; price: string; quantity: number }>
    transactionid?: string
  }
}

/**
 * ログインユーザーのメールアドレスを取得してSHA-256ハッシュ化する
 * @returns ハッシュ化されたメールアドレス（取得できない場合は空文字列）
 */
const fetchHashedEmail = async (): Promise<string> => {
  try {
    const res = await fetch('/api/members/me', { credentials: 'same-origin' })

    if (res.ok) {
      const { mail } = (await res.json()) as { mail?: string }

      if (mail !== undefined && mail !== '') {
        // メールアドレスをSHA-256でハッシュ化
        const encoder = new TextEncoder()
        const data = encoder.encode(mail)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
        return hashHex
      } else {
        console.error('Criteo: ログインユーザーのメールアドレスが取得できませんでした')
        return ''
      }
    } else if (res.status !== 401) {
      // 401以外のエラーの場合はログ出力
      console.error('Criteo: ユーザー情報の取得に失敗しました', res.status)
      return ''
    }

    // 401の場合は未ログイン状態として扱う（エラーログなし）
    return ''
  } catch (e) {
    console.error('Criteo: API通信エラー', e)
    return ''
  }
}

/**
 * CriteoイベントをdataLayerにpushする
 * @param eventName Criteoイベント名
 * @param products 商品情報（オプション）
 * @param transactionid トランザクションID（オプション）
 */
export const pushCriteoEvent = async (
  eventName: string,
  options?: {
    products?: Array<{ id: string; price: string; quantity: number }>
    transactionid?: string
  }
): Promise<void> => {
  const dataLayer = (window as any).dataLayer ?? ((window as any).dataLayer = [])
  const email = await fetchHashedEmail()

  const event: CriteoEvent = {
    event: eventName,
    crto: {
      email,
      ...(options?.products !== undefined && { products: options.products }),
      ...(options?.transactionid !== undefined && { transactionid: options.transactionid })
    }
  }
  dataLayer.push(event)
}
